import React from 'react';
import { Box } from '@styleseat/ui';
import { colors } from '@styleseat/brand';
import type { ProSpecialty } from '../../../../../modules/Api.types';
import { BodyText, Stack } from '../../../../ui';
import ProProfileHighlights from '../../ProProfileHighlights';
import { useResponsive } from '../../../../../hooks/useResponsiveEffect';
import { PublicProvider } from '../../../../../api/Providers';
import { ClientProviderProfile } from '../../../../../api/ProfileClientView';

interface CardResizeEvent {
  title: string;
  index: number;
}

type ProPersonalInfoCardProps = {
  provider: PublicProvider | ClientProviderProfile;
  specialties: ProSpecialty[];
  aboutMe?: React.ReactNode;
  onExpandHighlight: (event: CardResizeEvent) => void;
  onCollapseHighlight: (event: CardResizeEvent) => void;
};

export const ProPersonalInfoCard: React.FCWithChildren<ProPersonalInfoCardProps> = ({
  provider,
  specialties,
  aboutMe,
  onExpandHighlight,
  onCollapseHighlight,
}) => {
  const [providerFirstName] = provider.name.split(' ');
  const pronouns = provider.preferred_pronouns;
  const pluralPronouns = pronouns && pronouns.includes('/');
  const are = pluralPronouns ? 'are' : 'is';
  const s = pluralPronouns ? 's' : '';
  const responsive = useResponsive();
  return (
    <Stack
      padding={12}
      color={colors.whiteWash}
      borderWidth={1}
      borderColor={colors.lightGrey}
      borderStyle="solid"
      space={8}
    >
      <Stack space={4}>
        <Box
          testID="provider-info-intro-text"
          style={{ flexDirection: responsive.mobile ? 'column' : 'row' }}
        >
          <BodyText testID="pro-greeting" weight="bold">Hi there, </BodyText>
          <BodyText testID="pro-greeting" weight="bold">I&apos;m {providerFirstName}</BodyText>
        </Box>
        {pronouns && (
          <BodyText appearance="legal" testID="provider-info-pronouns">
            My pronoun{s} {are} {pronouns}.
          </BodyText>
        )}
        <BodyText appearance="legal" testID="provider-info-joined-on-text">
          I joined StyleSeat in {new Date(provider.creation_time).getFullYear()}
        </BodyText>
      </Stack>
      <ProProfileHighlights
        provider={provider}
        specialties={specialties}
        onExpandHighlight={onExpandHighlight}
        onCollapseHighlight={onCollapseHighlight}
      />
      {aboutMe ? (
        <Stack paddingTop={8}>
          {aboutMe}
        </Stack>
      ) : null}
    </Stack>
  );
};

export default ProPersonalInfoCard;
